import React, { useState, useRef } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';

interface ImageWithFallbackProps {
  src: string;
  alt: string;
  height?: number | string;
  width?: number | string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  onClick?: () => void;
  onLoad?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
  src,
  alt,
  height = 300,
  width = '100%',
  objectFit = 'cover',
  onClick,
  onLoad,
  className,
  style,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
    onLoad?.();
  };

  const handleError = () => {
    setIsLoaded(false);
    setHasError(true);
    console.error('Failed to load image:', src);
  };

  return (
    <Box sx={{ ...styles.container, height, width }}>
      {/* Skeleton loader while image is loading */}
      {!isLoaded && !hasError && <Skeleton variant='rectangular' width='100%' height='100%' animation='pulse' sx={styles.skeleton} />}

      {/* Image */}
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        onClick={onClick}
        className={className}
        style={{
          ...styles.image,
          objectFit,
          opacity: isLoaded ? 1 : 0,
          cursor: onClick ? 'pointer' : 'default',
          ...style,
        }}
      />

      {/* Error state */}
      {hasError && (
        <Box sx={styles.errorContainer}>
          <Typography variant='body2' align='center'>
            Failed to load image
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const styles = {
  container: {
    position: 'relative' as const,
  },
  skeleton: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    zIndex: 1,
  },
  image: {
    width: '100%',
    height: '100%',
    backgroundColor: 'grey.100',
    margin: 0,
    padding: 0,
    borderRadius: 0,
    transition: 'opacity 0.3s ease-in-out',
  },
  errorContainer: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'grey.200',
    color: 'text.secondary',
    zIndex: 1,
  },
};

export default ImageWithFallback;
